import { useQuery } from '@apollo/client';
import { Autocomplete, Box, TextField, type AutocompleteProps } from '@mui/material';
import { Duration } from 'luxon';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { namedByPlaceholder } from '~/components/forms/autocomplete-label';
import { useAppContext } from '~/contexts';
import { AvailableOverlaysDocument } from './OverlayAutocomplete.generated';

export interface OverlayAutocompleteProps
  extends Omit<AutocompleteProps<number, false, false, false>, 'options' | 'renderInput'> {
  error?: boolean;
  helperText?: React.ReactNode;
  name?: string;
}

export const OverlayAutocomplete = ({
  error,
  helperText,
  name,
  ...autocompleteProps
}: OverlayAutocompleteProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();

  const { data, loading } = useQuery(AvailableOverlaysDocument, {
    variables: { networkId: currentNetwork.id },
  });

  const overlaysById = useMemo(() => {
    return new Map(data?.network?.overlays.map((overlay) => [overlay.id, overlay] as const) ?? []);
  }, [data?.network?.overlays]);

  const options = useMemo(() => [...overlaysById.keys()], [overlaysById]);

  if (loading && !data?.network?.overlays) return null;

  return (
    <Autocomplete<number>
      fullWidth
      getOptionLabel={(option) => {
        const overlay = overlaysById.get(option);
        if (!overlay) return '';
        return `${overlay.contentItem.name} (${Duration.fromISO(overlay.duration).toHuman()})`;
      }}
      loading={loading}
      options={options}
      renderInput={(params) => (
        <TextField
          error={error}
          helperText={helperText}
          name={name}
          {...namedByPlaceholder(
            params,
            t('pages.device_signals.settings.placeholders.select_overlay'),
          )}
        />
      )}
      renderOption={(props, option) => {
        const overlay = overlaysById.get(option);
        return (
          <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
            {overlay && (
              <>
                {overlay.contentItem.thumbnailUri && (
                  <img
                    loading="lazy"
                    width="20"
                    srcSet={overlay.contentItem.thumbnailUri}
                    src={overlay.contentItem.thumbnailUri}
                    alt=""
                  />
                )}
                {overlay.contentItem.name} ({Duration.fromISO(overlay.duration).toHuman()})
              </>
            )}
          </Box>
        );
      }}
      {...autocompleteProps}
    />
  );
};
